/**
 * Hogarmola · capa de movimiento
 * GSAP 3.13 (núcleo, ScrollTrigger, SplitText) y Lenis 1.3, alojados en assets/.
 *
 * - Scroll suave con Lenis, conectado a ScrollTrigger tal y como indica la documentación:
 *     lenis.on('scroll', ScrollTrigger.update);
 *     gsap.ticker.add((t) => lenis.raf(t * 1000));
 *     gsap.ticker.lagSmoothing(0);
 *   No se activa en el editor de temas ni con «reducir movimiento».
 * - Titular de la portada por palabras (SplitText), entradas suaves al hacer scroll,
 *   fotos con paralaje, cinta de ventajas, historia fija de Navidad, piezas que crecen,
 *   botones «magnéticos» e inclinación de las fichas (solo escritorio con ratón).
 * - Horizon 4.3 hace scroll dentro de .page-wrapper en escritorio (≥990 px) y en la ventana en móvil:
 *   Lenis y ScrollTrigger usan el mismo contenedor que el tema y se rehacen al cambiar de uno a otro.
 * - Con «reducir movimiento» no se mueve nada y todo el contenido se ve desde el principio.
 * - Si este archivo o GSAP no cargan, hm-head.liquid quita la clase hm-anim y el contenido se ve igual.
 * - En el editor de temas se vuelve a montar al cargar o quitar una sección.
 * - No toca los elementos de Horizon (carrito, selectores de variante, compra rápida).
 */
(function () {
  'use strict';

  var root = document.documentElement;
  var gsap = window.gsap;
  var ScrollTrigger = window.ScrollTrigger;
  var SplitText = window.SplitText;

  if (!gsap || !ScrollTrigger) {
    root.classList.remove('hm-anim');
    return;
  }
  if (window.hogarmolaMotion) return;

  gsap.registerPlugin(ScrollTrigger);
  if (SplitText) gsap.registerPlugin(SplitText);

  var designMode = root.classList.contains('shopify-design-mode') || !!(window.Shopify && window.Shopify.designMode);
  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var CONDITIONS = {
    motion: '(prefers-reduced-motion: no-preference)',
    wide: '(min-width: 990px)',
    fine: '(hover: hover) and (pointer: fine) and (min-width: 990px)'
  };
  var EASE = 'power3.out';

  /* ---------------- Contenedor de scroll (igual que assets/scroll-container.js de Horizon) ---------------- */
  var squeezeQuery = window.matchMedia('(min-width: 990px)');

  function scrollerEl() {
    var pw = document.querySelector('.page-wrapper');
    if (pw && squeezeQuery.matches && /(auto|scroll)/.test(window.getComputedStyle(pw).overflowY)) return pw;
    return null;
  }

  /* ---------------- Lenis ---------------- */
  var lenis = null;
  var lenisRaf = function (t) {
    if (lenis) lenis.raf(t * 1000);
  };
  var lockObserver = null;

  function startLenis() {
    if (lenis || designMode || reduceQuery.matches || typeof window.Lenis !== 'function') return;
    var wrapper = scrollerEl();
    var options = {
      anchors: true,
      prevent: function (node) {
        return !!(
          node &&
          node.closest &&
          node.closest('dialog, [role="dialog"], [data-lenis-prevent], .drawer, .menu-drawer, .cart-drawer, .quick-add-modal, .search-modal')
        );
      }
    };
    if (wrapper) {
      options.wrapper = wrapper;
      options.content = wrapper;
    }
    lenis = new window.Lenis(options);
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add(lenisRaf);
    gsap.ticker.lagSmoothing(0);

    // Horizon bloquea el scroll con html[scroll-lock] al abrir el carrito o el menú
    lockObserver = new MutationObserver(function () {
      if (!lenis) return;
      if (root.hasAttribute('scroll-lock')) lenis.stop();
      else lenis.start();
    });
    lockObserver.observe(root, { attributes: true, attributeFilter: ['scroll-lock'] });
  }

  function stopLenis() {
    if (!lenis) return;
    gsap.ticker.remove(lenisRaf);
    if (lockObserver) lockObserver.disconnect();
    lenis.destroy();
    lenis = null;
  }

  /* ---------------- Utilidades ---------------- */
  function all(scope, selector) {
    return Array.prototype.slice.call(scope.querySelectorAll(selector));
  }

  function on(cleanups, el, type, fn, opts) {
    el.addEventListener(type, fn, opts || false);
    cleanups.push(function () {
      el.removeEventListener(type, fn, opts || false);
    });
  }

  /* Entradas: titular, luego texto, luego imagen, de 60 a 90 ms entre sí, una sola vez */
  function reveals(scope) {
    var heads = all(scope, '[data-hm-reveal]');
    var items = all(scope, '[data-hm-reveal-item]');

    if (heads.length) {
      gsap.set(heads, { autoAlpha: 0, y: 26 });
      ScrollTrigger.batch(heads, {
        start: 'top 90%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.9, ease: EASE, stagger: 0.07, overwrite: true });
        }
      });
    }

    if (items.length) {
      gsap.set(items, { autoAlpha: 0, y: 40 });
      ScrollTrigger.batch(items, {
        start: 'top 92%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { autoAlpha: 1, y: 0, duration: 1, ease: EASE, stagger: 0.085, overwrite: true });
        }
      });
    }
  }

  /* Botones que se acercan al puntero */
  function magnetic(scope, cleanups) {
    all(scope, '[data-hm-magnetic]').forEach(function (btn) {
      var xTo = gsap.quickTo(btn, 'x', { duration: 0.5, ease: 'power3' });
      var yTo = gsap.quickTo(btn, 'y', { duration: 0.5, ease: 'power3' });
      on(cleanups, btn, 'pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        xTo((e.clientX - (r.left + r.width / 2)) * 0.22);
        yTo((e.clientY - (r.top + r.height / 2)) * 0.32);
      });
      on(cleanups, btn, 'pointerleave', function () {
        xTo(0);
        yTo(0);
      });
      on(cleanups, btn, 'pointerdown', function () {
        gsap.to(btn, { scale: 0.96, duration: 0.15, ease: 'power2.out' });
      });
      on(cleanups, btn, 'pointerup', function () {
        gsap.to(btn, { scale: 1, duration: 0.4, ease: 'back.out(3)' });
      });
    });
  }

  /* Fichas del escaparate: se inclinan un poco hacia el puntero */
  function tilt(scope, cleanups) {
    all(scope, '[data-hm-tilt]').forEach(function (card) {
      var tile = card.querySelector('.hm-spec__tile');
      if (!tile) return;
      gsap.set(tile, { transformPerspective: 900 });
      var rx = gsap.quickTo(tile, 'rotationX', { duration: 0.6, ease: 'power3' });
      var ry = gsap.quickTo(tile, 'rotationY', { duration: 0.6, ease: 'power3' });
      on(cleanups, card, 'pointermove', function (e) {
        var r = tile.getBoundingClientRect();
        ry(((e.clientX - r.left) / r.width - 0.5) * 7);
        rx(-((e.clientY - r.top) / r.height - 0.5) * 7);
      });
      on(cleanups, card, 'pointerleave', function () {
        rx(0);
        ry(0);
      });
    });
  }

  /* ---------------- Secciones ---------------- */
  var builders = {
    hero: function (el) {
      var title = el.querySelector('.hm-hero__title');
      var items = all(el, '[data-hm-hero-item]');
      var img = el.querySelector('.hm-hero__img');
      var copy = el.querySelector('.hm-hero__copy');
      var tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      if (img) tl.fromTo(img, { scale: 1.12 }, { scale: 1, duration: 2.4, ease: 'power2.out' }, 0);

      if (title && SplitText) {
        gsap.set(title, { autoAlpha: 1 });
        SplitText.create(title, {
          type: 'lines,words',
          mask: 'lines',
          linesClass: 'hm-line',
          autoSplit: true,
          onSplit: function (self) {
            return gsap.from(self.words, {
              yPercent: 115,
              rotation: 4,
              duration: 1.15,
              ease: 'power4.out',
              stagger: 0.07,
              delay: 0.15
            });
          }
        });
      } else if (title) {
        tl.from(title, { autoAlpha: 0, y: 30, duration: 1 }, 0.1);
      }

      if (items.length) tl.from(items, { autoAlpha: 0, y: 24, duration: 0.9, stagger: 0.1 }, 0.6);

      // Al salir de la portada: la foto baja un poco más despacio que el texto
      if (img) {
        gsap.to(img, {
          yPercent: 9,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
      if (copy) {
        gsap.to(copy, {
          y: -70,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
    },

    marquee: function (el, cleanups) {
      var track = el.querySelector('[data-hm-marquee]');
      if (!track) return;
      track.setAttribute('data-hm-running', '');
      cleanups.push(function () {
        track.removeAttribute('data-hm-running');
      });
      var loop = gsap.to(track, { xPercent: -50, ease: 'none', duration: 34, repeat: -1 });
      var settle = null;
      ScrollTrigger.create({
        trigger: el,
        start: 'top bottom',
        end: 'bottom top',
        onToggle: function (self) {
          if (self.isActive) loop.play();
          else loop.pause();
        },
        onUpdate: function (self) {
          // Como la cinta de React Bits «Scroll Velocity»: acelera con la velocidad del scroll
          var boost = gsap.utils.clamp(1, 5, 1 + Math.abs(self.getVelocity()) / 700);
          gsap.to(loop, { timeScale: boost, duration: 0.2, overwrite: true });
          if (settle) settle.kill();
          settle = gsap.to(loop, { timeScale: 1, duration: 1.2, delay: 0.2 });
        }
      });
    },

    feature: function (el) {
      var img = el.querySelector('.hm-tile--photo .hm-tile__img');
      if (img) {
        gsap.fromTo(
          img,
          { yPercent: -7, scale: 1.1 },
          {
            yPercent: 7,
            scale: 1,
            ease: 'none',
            scrollTrigger: { trigger: img.parentNode, start: 'top bottom', end: 'bottom top', scrub: true }
          }
        );
      }
      var num = el.querySelector('.hm-figure__num');
      if (num) {
        gsap.from(num, {
          scale: 0.4,
          rotation: -12,
          transformOrigin: '20% 90%',
          duration: 1.1,
          ease: 'back.out(1.8)',
          scrollTrigger: { trigger: num, start: 'top 90%', once: true }
        });
      }
    },

    navidad: function (el, cleanups, c) {
      if (!c.wide) return;
      var imgs = all(el, '.hm-navidad__stage-img');
      var steps = all(el, '[data-hm-story-item]');
      if (imgs.length < 2 || !steps.length) return;
      var current = 0;
      gsap.set(imgs, { autoAlpha: function (i) { return i === 0 ? 1 : 0; } });
      var drift = gsap.fromTo(imgs[0], { scale: 1.08 }, { scale: 1, duration: 6, ease: 'none' });

      function show(i) {
        if (i === current || !imgs[i]) return;
        current = i;
        if (drift) drift.kill();
        gsap.to(imgs, {
          autoAlpha: function (k) { return k === i ? 1 : 0; },
          duration: 0.9,
          ease: 'power2.out',
          overwrite: 'auto'
        });
        drift = gsap.fromTo(imgs[i], { scale: 1.08 }, { scale: 1, duration: 6, ease: 'none' });
      }

      steps.forEach(function (step) {
        var i = parseInt(step.getAttribute('data-hm-story-item'), 10) || 0;
        ScrollTrigger.create({
          trigger: step,
          start: 'top 60%',
          end: 'bottom 60%',
          onToggle: function (self) {
            if (self.isActive) show(i);
          }
        });
      });
    },

    steps: function (el) {
      var tiles = all(el, '[data-hm-grow]');
      if (!tiles.length) return;
      gsap.fromTo(
        tiles,
        { clipPath: 'inset(100% 0% 0% 0% round 22px)' },
        {
          clipPath: 'inset(0% 0% 0% 0% round 22px)',
          duration: 1.2,
          ease: 'power4.out',
          stagger: 0.16,
          scrollTrigger: { trigger: tiles[0].closest('ol') || el, start: 'top 82%', once: true }
        }
      );
      tiles.forEach(function (tile) {
        var img = tile.querySelector('img');
        if (!img) return;
        gsap.fromTo(
          img,
          { yPercent: 6 },
          { yPercent: -6, ease: 'none', scrollTrigger: { trigger: tile, start: 'top bottom', end: 'bottom top', scrub: true } }
        );
      });
    },

    promise: function (el) {
      var card = el.querySelector('[data-hm-card]');
      if (!card) return;
      gsap.fromTo(
        card,
        { rotation: 3, y: 120 },
        {
          rotation: -1.5,
          y: 0,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 25%', scrub: 0.8 }
        }
      );
    },

    faq: function (el, cleanups) {
      all(el, 'details').forEach(function (d) {
        var answer = d.querySelector('.hm-faq__a');
        if (!answer) return;
        on(cleanups, d, 'toggle', function () {
          if (d.open) gsap.from(answer, { autoAlpha: 0, y: -10, duration: 0.45, ease: 'power2.out' });
        });
      });
    },

    cta: function (el) {
      all(el, '.hm-cta__img').forEach(function (img, i) {
        var amount = [6, 10, 4][i % 3];
        gsap.fromTo(
          img,
          { yPercent: -amount },
          {
            yPercent: amount,
            ease: 'none',
            scrollTrigger: { trigger: el, start: 'top bottom', end: 'center top', scrub: true }
          }
        );
      });
    },

    /* ---- v5 ---- */

    /* «Un problema menos»: titular por palabras y la baldosa que se abre como un obturador */
    specimen: function (el, cleanups, c) {
      var title = el.querySelector('[data-hm-spec-title]');
      var tile = el.querySelector('.hm-spec5__tile');
      var names = all(el, '.hm-spec5__name');
      var facts = el.querySelector('.hm-spec5__facts');
      var tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

      if (title && SplitText) {
        gsap.set(title, { autoAlpha: 1 });
        SplitText.create(title, {
          type: 'lines,words',
          mask: 'lines',
          linesClass: 'hm-line',
          autoSplit: true,
          onSplit: function (self) {
            return gsap.from(self.words, { yPercent: 115, rotation: 3, duration: 1.1, ease: 'power4.out', stagger: 0.06, delay: 0.1 });
          }
        });
      } else if (title) {
        tl.fromTo(title, { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.9 }, 0.1);
      }

      if (tile) {
        tl.fromTo(
          tile,
          { autoAlpha: 1, clipPath: 'inset(100% 0% 0% 0% round 20px)', scale: 1.06 },
          { clipPath: 'inset(0% 0% 0% 0% round 20px)', scale: 1, duration: 1.25, ease: 'power3.inOut' },
          0.25
        );
        tl.set(tile, { clearProps: 'clipPath,scale' });
      }
      if (names.length) {
        tl.from(names, { autoAlpha: 0, x: c.wide ? 24 : 0, y: c.wide ? 0 : 12, duration: 0.7, stagger: 0.05, ease: 'power3.out' }, 0.7);
      }
      if (facts) tl.from(facts, { autoAlpha: 0, y: 24, duration: 0.8, ease: 'power3.out' }, 0.95);

      // Al bajar, la baldosa sube un poco más despacio que el texto
      if (tile && c.wide) {
        gsap.to(tile, {
          yPercent: -8,
          ease: 'none',
          scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true }
        });
      }
    },

    /* Catálogo: los círculos de las fotos giran y crecen al entrar, fila a fila */
    catalogo: function (el) {
      var pics = all(el, '.hm-cat5__pic');
      if (!pics.length) return;
      gsap.set(pics, { scale: 0.55, rotation: -18 });
      ScrollTrigger.batch(pics, {
        start: 'top 92%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, { scale: 1, rotation: 0, duration: 1, ease: 'back.out(1.6)', stagger: 0.08, overwrite: true });
        }
      });
    },

    /* Producto en círculo: el círculo crece y gira un poco mientras entra */
    disco: function (el, cleanups, c) {
      var disc = el.querySelector('.hm-disco5__disc');
      var names = all(el, '.hm-disco5__name');
      var side = el.querySelector('.hm-disco5__side');
      if (disc) {
        gsap.fromTo(
          disc,
          { scale: 0.78, rotation: -10 },
          { scale: 1, rotation: 0, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'center center', scrub: 0.6 } }
        );
      }
      if (names.length) {
        gsap.from(names, {
          autoAlpha: 0,
          y: c.wide ? 26 : 0,
          duration: 0.8,
          ease: EASE,
          stagger: 0.07,
          scrollTrigger: { trigger: names[0], start: 'top 92%', once: true }
        });
      }
      if (side) {
        gsap.from(side, { autoAlpha: 0, y: 24, duration: 0.9, ease: EASE, scrollTrigger: { trigger: el, start: 'top 55%', once: true } });
      }
    },

    /* Escena fija: la foto se acerca muy despacio mientras pasan las frases */
    escena: function (el) {
      var img = el.querySelector('.hm-esc5__bg-img');
      var track = el.querySelector('.hm-esc5__track');
      if (!img || !track) return;
      gsap.fromTo(
        img,
        { scale: 1.14 },
        { scale: 1, ease: 'none', scrollTrigger: { trigger: track, start: 'top bottom', end: 'bottom bottom', scrub: true } }
      );
    },

    /* Antes de pedir: la foto alta se desliza dentro de su marco y la cuadrada sube */
    ayuda: function (el) {
      var tall = el.querySelector('.hm-ayu5__tall .hm-ayu5__img');
      var square = el.querySelector('.hm-ayu5__square');
      if (tall) {
        gsap.fromTo(
          tall,
          { yPercent: -6, scale: 1.12 },
          { yPercent: 6, scale: 1.12, ease: 'none', scrollTrigger: { trigger: tall.parentNode, start: 'top bottom', end: 'bottom top', scrub: true } }
        );
      }
      if (square) {
        gsap.fromTo(
          square,
          { y: 90, rotation: -4 },
          { y: 0, rotation: 0, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'center center', scrub: 0.6 } }
        );
      }
    },

    /* Cierre: el invento entra girando desde abajo y se queda cortado por el borde */
    cierre: function (el) {
      var img = el.querySelector('.hm-cie5__img');
      if (!img) return;
      gsap.fromTo(
        img,
        { rotation: -44, yPercent: 22 },
        { rotation: -24, yPercent: 0, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'center center', scrub: 0.8 } }
      );
    },

    /* Sobre nosotros: la nota se pega en la nevera y las molestias se tachan una a una */
    nosotros: function (el) {
      var note = el.querySelector('[data-hm-note]');
      if (!note) return;
      var tl = gsap.timeline({ scrollTrigger: { trigger: note, start: 'top 78%', once: true } });
      tl.from(note, { rotation: -8, y: 48, autoAlpha: 0, duration: 1.1, ease: EASE });
      // Línea a línea: se tacha la molestia y, justo después, aparece el producto que la arregla
      all(note, '.hm-about__item').forEach(function (item, i) {
        var line = item.querySelector('.hm-about__done');
        var fix = item.querySelector('.hm-about__fix');
        var at = 0.75 + i * 0.22;
        if (line) {
          gsap.set(line, { backgroundSize: '0% 3px' });
          tl.to(line, { backgroundSize: '100% 3px', duration: 0.45, ease: 'power2.inOut' }, at);
        }
        if (fix) tl.from(fix, { autoAlpha: 0, x: -10, duration: 0.4, ease: EASE }, at + 0.35);
      });
    },

    /* Pie postal: la postal sube y se endereza al llegar */
    postal: function (el) {
      var card = el.querySelector('[data-hm-postal]');
      if (!card) return;
      gsap.fromTo(
        card,
        { y: 70, rotation: -1.6 },
        { y: 0, rotation: 0, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'top 35%', scrub: 0.6 } }
      );
    }
  };

  /* Secciones de Horizon (fichas, páginas): solo titulares y fotos de las secciones
     de descripción, nunca la sección principal del producto ni el carrito. */
  function horizonReveals(scope) {
    var targets = all(scope, 'h2').filter(function (h) {
      return !h.closest('[data-hm-section], .product-information, dialog, header, footer, .product-card, product-card');
    });
    if (!targets.length) return;
    gsap.set(targets, { autoAlpha: 0, y: 24 });
    ScrollTrigger.batch(targets, {
      start: 'top 92%',
      once: true,
      onEnter: function (batch) {
        gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.85, ease: EASE, stagger: 0.07, overwrite: true });
      }
    });
  }

  /* ---------------- Montaje por sección ---------------- */
  var registry = new Map();

  function setup(el) {
    teardown(el);
    var mm = gsap.matchMedia();
    mm.add(
      CONDITIONS,
      function (ctx) {
        var c = ctx.conditions;
        if (!c.motion) return;
        var cleanups = [];
        var kind = el.getAttribute('data-hm-section');
        if (kind) {
          reveals(el);
          if (builders[kind]) builders[kind](el, cleanups, c);
        } else {
          horizonReveals(el);
        }
        if (c.fine) {
          magnetic(el, cleanups);
          tilt(el, cleanups);
        }
        return function () {
          cleanups.forEach(function (fn) {
            fn();
          });
        };
      },
      el
    );
    registry.set(el, mm);
  }

  function teardown(el) {
    var mm = registry.get(el);
    if (mm) {
      mm.revert();
      registry.delete(el);
    }
  }

  function sectionsIn(node) {
    var list = [];
    if (!node || !node.querySelectorAll) return list;
    if (node.matches && node.matches('[data-hm-section]')) list.push(node);
    return list.concat(all(node, '[data-hm-section]'));
  }

  function setupAll() {
    ScrollTrigger.defaults({ scroller: scrollerEl() || window });
    sectionsIn(document).forEach(setup);
    var main = document.getElementById('MainContent');
    if (main) {
      all(main, '.shopify-section').forEach(function (section) {
        if (!section.querySelector('[data-hm-section]')) setup(section);
      });
    }
  }

  function teardownAll() {
    Array.from(registry.keys()).forEach(teardown);
  }

  function init() {
    setupAll();
    startLenis();
    root.classList.add('hm-motion-ready');

    // Las tipografías y las fotos cambian alturas: recalcular posiciones cuando lleguen
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
    window.addEventListener('load', function () { ScrollTrigger.refresh(); });

    if (reduceQuery.addEventListener) {
      reduceQuery.addEventListener('change', function () {
        if (reduceQuery.matches) stopLenis();
        else startLenis();
      });
    }

    // Al pasar de móvil a escritorio (o al revés) cambia el contenedor de scroll: rehacer todo
    if (squeezeQuery.addEventListener) {
      squeezeQuery.addEventListener('change', function () {
        stopLenis();
        teardownAll();
        setupAll();
        startLenis();
        ScrollTrigger.refresh();
      });
    }
  }

  /* Editor de temas: montar y desmontar al cargar o quitar secciones */
  document.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    var list = sectionsIn(target);
    if (!list.length && target.classList && target.classList.contains('shopify-section')) list = [target];
    list.forEach(setup);
    ScrollTrigger.refresh();
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var target = event.target;
    sectionsIn(target).forEach(teardown);
    if (registry.has(target)) teardown(target);
  });

  window.hogarmolaMotion = { refresh: function () { ScrollTrigger.refresh(); }, lenis: function () { return lenis; } };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
